import type { LicenceType, MedicalClass } from './licences'

// Who may fly what, by licence and medical -- ported from jupi-fsm-app's
// frontend/src/lib/licenses.ts, where it ran in the browser only. Shared
// now: the briefing screen shows the same answers the API enforces.
//
// `today` is YYYY-MM-DD in the club's timezone, passed in by the caller;
// these functions never read a clock.

export interface GateLicence {
  licenceType: LicenceType
  held: boolean | null
  expiryDate: string | null
  medicalClass: MedicalClass | null
  documentFilename: string | null
}

export interface LicenceIssue {
  licenceType: LicenceType
  reason: 'expired' | 'missing' | 'wrong_class'
}

export interface GateResult {
  blocked: boolean
  issues: LicenceIssue[]
}

const MEDICAL_RANK: Record<MedicalClass, number> = { lapl: 0, class2: 1, class1: 2 }

// A member may hold several medicals at once (member.user_licences has one
// row per class), so "the medical" is a choice: a current one beats an
// expired one, then the higher class, then the later expiry. A medical
// with no expiry date recorded counts as missing. Every gate below asks
// for this one, so they agree with each other.
export function bestMedical(licences: GateLicence[], today: string): GateLicence | undefined {
  const medicals = licences.filter((l) => l.licenceType === 'medical' && l.expiryDate)
  const rank = (l: GateLicence) => (l.medicalClass ? MEDICAL_RANK[l.medicalClass] : -1)
  return medicals.sort((a, b) => {
    const aCurrent = !expired(a.expiryDate, today)
    const bCurrent = !expired(b.expiryDate, today)
    if (aCurrent !== bCurrent) return aCurrent ? -1 : 1
    if (rank(a) !== rank(b)) return rank(b) - rank(a)
    return (b.expiryDate ?? '').localeCompare(a.expiryDate ?? '')
  })[0]
}

// The licence rows other than medicals, by type (a medical is not unique).
const byType = (licences: GateLicence[]) =>
  new Map(licences.filter((l) => l.licenceType !== 'medical').map((l) => [l.licenceType, l]))
const expired = (date: string | null, today: string) => date !== null && date < today

// A rating the flight needs: explicitly "not held" is the same as never
// recorded -- held=false is for the profile's display, never a way round
// a gate.
function ratingIssue(licence: GateLicence | undefined, type: LicenceType, today: string): LicenceIssue | null {
  if (licence?.held === false || !licence?.expiryDate) return { licenceType: type, reason: 'missing' }
  if (expired(licence.expiryDate, today)) return { licenceType: type, reason: 'expired' }
  return null
}

// What the instructor must hold, by the programme taught (the club's table,
// issue #123): a Class 2 (or better) medical and SEP(land) always; then FI
// for PPL, LAPL, night flying and aerobatics (CRI would also do for the last
// two, but is not a rating we record), IR and IRI for the BIR.
const INSTRUCTOR_RATINGS: Record<string, LicenceType[]> = {
  BIR: ['sep', 'ir', 'iri'],
}
const DEFAULT_INSTRUCTOR_RATINGS: LicenceType[] = ['sep', 'fi']

// To instruct a course: the licence on file, a current Class 2 or better
// medical, and the ratings above, each current. A lapsed rating blocks as
// hard as a lapsed medical. An FI(A)* holds an FI rating and passes the same
// gate; what they may not do (release a student for solo) is separate.
export function instructorGate(licences: GateLicence[], today: string, opts: { courseCode?: string | null } = {}): GateResult {
  const map = byType(licences)
  const issues: LicenceIssue[] = []
  const medical = bestMedical(licences, today)
  if (!medical?.expiryDate) issues.push({ licenceType: 'medical', reason: 'missing' })
  else if (expired(medical.expiryDate, today)) issues.push({ licenceType: 'medical', reason: 'expired' })
  else if (!medical.medicalClass || MEDICAL_RANK[medical.medicalClass] < MEDICAL_RANK.class2) {
    issues.push({ licenceType: 'medical', reason: 'wrong_class' })
  }
  if (!map.get('licence')?.documentFilename) issues.push({ licenceType: 'licence', reason: 'missing' })
  for (const type of INSTRUCTOR_RATINGS[opts.courseCode ?? ''] ?? DEFAULT_INSTRUCTOR_RATINGS) {
    const issue = ratingIssue(map.get(type), type, today)
    if (issue) issues.push(issue)
  }
  return { blocked: issues.length > 0, issues }
}

// A student's medical or SEP blocks only when actually expired: early in
// training most students have neither, and that is normal.
export function studentGate(licences: GateLicence[], today: string): GateResult {
  const map = byType(licences)
  const issues: LicenceIssue[] = []
  if (expired(bestMedical(licences, today)?.expiryDate ?? null, today)) {
    issues.push({ licenceType: 'medical', reason: 'expired' })
  }
  if (expired(map.get('sep')?.expiryDate ?? null, today)) issues.push({ licenceType: 'sep', reason: 'expired' })
  return { blocked: issues.length > 0, issues }
}

// PPL(A) solo needs at least a Class 2 medical, LAPL(A) at least LAPL;
// other courses just a valid one.
const SOLO_MIN_MEDICAL: Partial<Record<string, MedicalClass>> = { PPL_A: 'class2', LAPL_A: 'lapl' }

export interface SoloMedicalIssue {
  reason: 'missing' | 'expired' | 'wrong_class'
  requiredClass?: MedicalClass
}

// Stricter than studentGate: nobody flies solo without a current medical
// of the right class on file. The one hard gate -- the API refuses to log
// a solo flight that fails it.
export function soloMedicalGate(licences: GateLicence[], today: string, courseCode?: string | null): SoloMedicalIssue | null {
  const medicals = licences.filter((l) => l.licenceType === 'medical' && l.expiryDate)
  if (medicals.length === 0) return { reason: 'missing' }
  const current = medicals.filter((l) => !expired(l.expiryDate, today))
  if (current.length === 0) return { reason: 'expired' }
  const required = courseCode ? SOLO_MIN_MEDICAL[courseCode] : undefined
  // Any one current medical of a sufficient class will do.
  if (required && !current.some((l) => l.medicalClass && MEDICAL_RANK[l.medicalClass] >= MEDICAL_RANK[required])) {
    return { reason: 'wrong_class', requiredClass: required }
  }
  return null
}

// #225: what a member is told when booking (and on their profile) about
// their own licence and medical. Recording them is optional, so no record at
// all is a reminder of the member's own legal responsibility, not a block.
export const LICENCE_NOTICE_DAYS = 30

export interface LicenceNoticeItem {
  licenceType: LicenceType
  status: 'expired' | 'expiring'
  expiryDate: string
}

export interface LicenceNotice {
  // Nothing about a licence or medical is on file.
  missing: boolean
  items: LicenceNoticeItem[]
}

// Every held row with an expiry that has passed or falls within
// LICENCE_NOTICE_DAYS. A member's several medicals are judged by the best
// one (bestMedical), so an old expired class does not warn next to a current
// one.
export function licenceNotice(licences: GateLicence[], today: string): LicenceNotice {
  const held = licences.filter((l) => l.held !== false)
  const medical = bestMedical(held, today)
  const rows = [...held.filter((l) => l.licenceType !== 'medical'), ...(medical ? [medical] : [])]
  const warnFrom = new Date(`${today}T00:00:00Z`)
  warnFrom.setUTCDate(warnFrom.getUTCDate() + LICENCE_NOTICE_DAYS)
  const horizon = warnFrom.toISOString().slice(0, 10)

  const items: LicenceNoticeItem[] = []
  for (const l of rows) {
    if (!l.expiryDate) continue
    if (l.expiryDate < today) items.push({ licenceType: l.licenceType, status: 'expired', expiryDate: l.expiryDate })
    else if (l.expiryDate <= horizon) items.push({ licenceType: l.licenceType, status: 'expiring', expiryDate: l.expiryDate })
  }
  const onFile = held.some((l) => l.expiryDate || l.documentFilename)
  return { missing: !onFile, items }
}
